import { Cascader, Select, Space, Typography } from 'antd';
import React, { useMemo } from 'react';
import type { AreaCascaderOption, AreaUuidPathMap } from '@/utils/taxonomy';

export type ResourceAreaValue = {
  areaUuid: string;
  areaName?: string;
  isPrimary?: boolean;
  sortOrder?: number;
};

type ResourceAreaSelectorProps = {
  value?: ResourceAreaValue[];
  onChange?: (value?: ResourceAreaValue[]) => void;
  options: AreaCascaderOption[];
  uuidPathMap: AreaUuidPathMap;
  disabled?: boolean;
  placeholder?: string;
};

const normalizeAreas = (areas?: ResourceAreaValue[]) =>
  Array.isArray(areas)
    ? areas
        .map((item, index) => ({
          areaUuid: String(item?.areaUuid || '').trim(),
          areaName: item?.areaName,
          isPrimary: item?.isPrimary === true,
          sortOrder: Number.isFinite(Number(item?.sortOrder))
            ? Number(item.sortOrder)
            : index,
        }))
        .filter((item) => item.areaUuid)
    : [];

export function ResourceAreaSelector({
  value,
  onChange,
  options,
  uuidPathMap,
  disabled,
  placeholder = '请选择区域',
}: ResourceAreaSelectorProps) {
  const areas = normalizeAreas(value);
  const selectedPaths = areas.map(
    (item) => uuidPathMap[item.areaUuid] || [item.areaUuid],
  );
  const primaryAreaUuid =
    areas.find((item) => item.isPrimary)?.areaUuid || areas[0]?.areaUuid;

  const primaryOptions = useMemo(
    () =>
      areas.map((item) => ({
        value: item.areaUuid,
        label: item.areaName || item.areaUuid,
      })),
    [areas],
  );

  const emit = (nextAreas: ResourceAreaValue[]) => {
    if (!nextAreas.length) {
      onChange?.([]);
      return;
    }
    const hasPrimary = nextAreas.some((item) => item.isPrimary);
    onChange?.(
      nextAreas.map((item, index) => ({
        areaUuid: item.areaUuid,
        areaName: item.areaName,
        sortOrder: index,
        isPrimary: hasPrimary ? item.isPrimary === true : index === 0,
      })),
    );
  };

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size={4}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Cascader
          multiple
          options={options}
          value={selectedPaths}
          changeOnSelect
          allowClear
          showSearch
          displayRender={(labels) => labels.join(' / ')}
          placeholder={placeholder}
          disabled={disabled}
          style={{ flex: '1 1 260px', minWidth: 0 }}
          onChange={(paths, selectedOptions) => {
            const nextAreas = (paths as Array<Array<string | number>>)
              .map((path, index) => {
                const areaUuid = String(path[path.length - 1] || '').trim();
                const optionPath = (selectedOptions?.[index] || []) as Array<{
                  label?: React.ReactNode;
                }>;
                return {
                  areaUuid,
                  areaName: optionPath
                    .map((item) => String(item.label || ''))
                    .filter(Boolean)
                    .join(' / '),
                  sortOrder: index,
                  isPrimary: areaUuid === primaryAreaUuid,
                };
              })
              .filter((item) => item.areaUuid);
            emit(nextAreas);
          }}
        />
        <Select
          value={primaryAreaUuid}
          options={primaryOptions}
          allowClear={false}
          disabled={disabled || !areas.length}
          placeholder="主区域"
          style={{ width: 220, maxWidth: '100%' }}
          onChange={(nextPrimary) => {
            emit(
              areas.map((item) => ({
                ...item,
                isPrimary: item.areaUuid === nextPrimary,
              })),
            );
          }}
        />
      </div>
      {!areas.length ? (
        <Typography.Text type="secondary">未选择区域</Typography.Text>
      ) : null}
    </Space>
  );
}
